/**
 * CategoryInvoiceTextFields.jsx (L1)
 * In der Maske der Leistungskategorie:
 * - Rechnungstext (Haupt- und Unterkategorie): Bezeichnung (BT-153), Vorlage der Beschreibung (BT-154),
 *   Aufwandshinweis. Bei Unterkategorien gelten leere Vorlage und leerer Hinweis von der Hauptkategorie.
 * - Tätigkeiten (nur Hauptkategorie): Liste für die Auswahl bei der Buchung, mit Text auf der Rechnung.
 */
import { useRef } from 'react';
import { ArrowUp, ArrowDown, Plus, EyeOff, Eye } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

export const PLACEHOLDERS = ['Zeitraum', 'Einsatzort', 'Tätigkeiten', 'Auftrag', 'Bestellnummer', 'Aufwandshinweis'];

export const DEFAULT_TEMPLATE =
  'Leistungszeitraum {Zeitraum}[, Einsatzort {Einsatzort}].\n' +
  'Erbrachte Leistungen: {Tätigkeiten}.\n' +
  '{Aufwandshinweis}';

const SAMPLE = {
  Zeitraum: '01.09.–30.09.2026',
  Einsatzort: 'Werk Stade',
  Auftrag: 'Regelbetreuung 2026',
  Bestellnummer: '',
};

/** Gleiche Regeln wie im Backend (position_text_service.render_template) – nur für die Vorschau */
export function renderTemplate(template, values) {
  let text = (template && template.trim()) ? template : DEFAULT_TEMPLATE;
  text = text.replace(/\[([^[\]]*)\]/g, (m, inner) => {
    const names = [...inner.matchAll(/\{([^{}]+)\}/g)].map((x) => x[1]);
    return names.some((n) => n in values && !(values[n] || '').trim()) ? '' : inner;
  });
  text = text.replace(/\{([^{}]+)\}/g, (m, name) => (name in values ? (values[name] || '') : m));
  return text
    .replace(/\r\n/g, '\n')
    .split('\n')
    .map((l) => l.replace(/[ \t]{2,}/g, ' ').trim().replace(/(^|[^.])\.\.(?!\.)/g, '$1.').replace(/\s+([.,;:])/g, '$1'))
    .filter((l) => l && !['.', ',', ':', ';'].includes(l))
    .join('\n');
}

function sampleActivities(activities) {
  const active = (activities || []).filter((a) => a.active !== false && (a.name || '').trim()).slice(0, 3);
  if (!active.length) return 'Begehung der Arbeitsstätten (17.09.): Produktion und Lager; Beratung: Unterweisung Gabelstapler';
  return active
    .map((a, i) => {
      let t = (a.invoice_text || '').trim() || a.name.trim();
      if (a.with_date !== false) t += i === 0 ? ' (10.09.)' : ' (17.09.)';
      if (a.with_note !== false) t += ': Notiz der Buchung';
      return t;
    })
    .join('; ');
}

export default function CategoryInvoiceTextFields({ isMain, value, onChange, parentCategory }) {
  const templateRef = useRef(null);
  const v = value || {};
  const activities = Array.isArray(v.activities) ? v.activities : [];

  const insertPlaceholder = (name) => {
    const current = v.invoice_template && v.invoice_template.trim() ? v.invoice_template : DEFAULT_TEMPLATE;
    const el = templateRef.current;
    const token = `{${name}}`;
    if (el && typeof el.selectionStart === 'number' && v.invoice_template) {
      onChange({ invoice_template: current.slice(0, el.selectionStart) + token + current.slice(el.selectionEnd) });
    } else {
      onChange({ invoice_template: `${current}${current.endsWith(' ') || current.endsWith('\n') ? '' : ' '}${token}` });
    }
  };

  const setActivities = (list) => onChange({ activities: list });
  const updateActivity = (idx, patch) => setActivities(activities.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  const moveActivity = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= activities.length) return;
    const list = [...activities];
    [list[idx], list[target]] = [list[target], list[idx]];
    setActivities(list);
  };
  const addActivity = () => setActivities([...activities, { name: '', invoice_text: '', with_date: true, with_note: true, active: true }]);
  const toggleActivity = (idx) => {
    const a = activities[idx];
    if (!a.id) {
      setActivities(activities.filter((_, i) => i !== idx)); // noch nicht gespeichert: einfach entfernen
    } else {
      updateActivity(idx, { active: a.active === false });
    }
  };

  const active = !!(v.invoice_title && v.invoice_title.trim());
  const inheritedTemplate = !isMain && !(v.invoice_template || '').trim() ? parentCategory?.invoice_template : null;
  const inheritedEffort = !isMain && !(v.effort_note || '').trim() ? parentCategory?.effort_note : null;
  const previewActivities = isMain ? activities : parentCategory?.activities;
  const preview = renderTemplate(v.invoice_template || inheritedTemplate, {
    ...SAMPLE,
    Tätigkeiten: sampleActivities(previewActivities),
    Aufwandshinweis: v.effort_note || inheritedEffort || '',
  });

  return (
    <div className="space-y-4">
      <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
        <div>
          <Label className="text-foreground text-sm font-medium">Rechnungstext</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Mit Bezeichnung werden die Stunden dieser Kategorie als eigene Position mit Beschreibung abgerechnet.
            {isMain
              ? ' Haben die Unterkategorien eine eigene Bezeichnung (z. B. Grund- und betriebsspezifische Betreuung), werden sie getrennte Positionen.'
              : ' Leere Vorlage und leerer Aufwandshinweis werden von der Hauptkategorie übernommen.'}
            {' '}Ohne Bezeichnung bleibt es wie bisher (Titel = Auftragsname).
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-muted-foreground">Bezeichnung der Position (BT-153)</Label>
          <Input
            value={v.invoice_title || ''}
            onChange={(e) => onChange({ invoice_title: e.target.value })}
            placeholder={isMain ? 'leer lassen, wenn die Unterkategorien eigene Bezeichnungen haben' : 'z. B. Sicherheitstechnische Betreuung nach DGUV Vorschrift 2 – Grundbetreuung'}
            className="bg-muted border-border text-foreground"
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-muted-foreground">Beschreibung – Vorlage (BT-154)</Label>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => onChange({ invoice_template: DEFAULT_TEMPLATE })}>
              Standardvorlage einsetzen
            </Button>
          </div>
          <Textarea
            ref={templateRef}
            rows={4}
            value={v.invoice_template || ''}
            onChange={(e) => onChange({ invoice_template: e.target.value })}
            placeholder={inheritedTemplate || DEFAULT_TEMPLATE}
            className="bg-muted border-border text-foreground font-mono text-xs"
          />
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-muted-foreground">Einfügen:</span>
            {PLACEHOLDERS.map((name) => (
              <Button key={name} type="button" variant="outline" size="sm" className="h-6 px-2 text-[11px] font-mono" onClick={() => insertPlaceholder(name)}>
                {name}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Eckige Klammern = optional: „[, Einsatzort {'{Einsatzort}'}]“ entfällt, wenn am Auftrag kein Standort hinterlegt ist.
            Leer = {isMain ? 'Standardvorlage' : 'Vorlage der Hauptkategorie'}.
          </p>
        </div>
        <div className="space-y-2">
          <Label className="text-muted-foreground">Aufwandshinweis</Label>
          <Textarea
            rows={3}
            value={v.effort_note || ''}
            onChange={(e) => onChange({ effort_note: e.target.value })}
            placeholder={inheritedEffort || 'z. B. Der ausgewiesene Zeitaufwand umfasst die jeweils erforderliche Vor- und Nachbereitung, die Recherche zu Rechtsgrundlagen und Regelwerk, Abstimmungen sowie die Dokumentation der Ergebnisse.'}
            className="bg-muted border-border text-foreground"
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-muted-foreground">Aufwandshinweis muss stehen bleiben</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Fehlt er nach einer Handänderung im Entwurf, warnt die Rechnungsprüfung.</p>
          </div>
          <Switch checked={!!v.effort_note_locked} onCheckedChange={(checked) => onChange({ effort_note_locked: checked })} />
        </div>
        {active && (
          <div className="rounded-md border border-border bg-card p-3 space-y-1">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Vorschau mit Beispielwerten</p>
            <p className="text-sm font-medium text-foreground">{v.invoice_title}</p>
            <p className="text-xs text-muted-foreground whitespace-pre-wrap">{preview}</p>
          </div>
        )}
      </div>

      {isMain && (
        <div className="space-y-3 p-3 rounded-lg border border-border bg-muted/30">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-foreground text-sm font-medium">Tätigkeiten</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Auswahl bei der Buchung (z. B. ASA-Sitzung, Begehung). Erscheinen im Platzhalter {'{Tätigkeiten}'} in dieser Reihenfolge.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Label className="text-xs text-muted-foreground">Pflicht bei der Buchung</Label>
              <Switch checked={!!v.activity_required} onCheckedChange={(checked) => onChange({ activity_required: checked })} />
            </div>
          </div>
          {activities.length > 0 && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-2 py-1.5">Tätigkeit</th>
                    <th className="text-left font-medium px-2 py-1.5">Text auf der Rechnung</th>
                    <th className="text-center font-medium px-2 py-1.5">Datum</th>
                    <th className="text-center font-medium px-2 py-1.5">Notiz</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a, idx) => (
                    <tr key={a.id || `neu-${idx}`} className={`border-t border-border ${a.active === false ? 'opacity-50' : ''}`}>
                      <td className="px-2 py-1.5 min-w-[130px]">
                        <Input
                          value={a.name || ''}
                          onChange={(e) => updateActivity(idx, { name: e.target.value })}
                          placeholder="z. B. Begehung"
                          className="h-8 bg-input border-border text-foreground"
                        />
                      </td>
                      <td className="px-2 py-1.5 min-w-[200px]">
                        <Input
                          value={a.invoice_text || ''}
                          onChange={(e) => updateActivity(idx, { invoice_text: e.target.value })}
                          placeholder={a.name || 'leer = Name'}
                          className="h-8 bg-input border-border text-foreground"
                        />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Checkbox checked={a.with_date !== false} onCheckedChange={(c) => updateActivity(idx, { with_date: !!c })} aria-label="Datum anhängen" />
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Checkbox checked={a.with_note !== false} onCheckedChange={(c) => updateActivity(idx, { with_note: !!c })} aria-label="Notiz anhängen" />
                      </td>
                      <td className="px-1 py-1.5 whitespace-nowrap text-right">
                        <Button type="button" variant="ghost" size="icon" className="w-7 h-7" onClick={() => moveActivity(idx, -1)} aria-label="nach oben" disabled={idx === 0}>
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button type="button" variant="ghost" size="icon" className="w-7 h-7" onClick={() => moveActivity(idx, 1)} aria-label="nach unten" disabled={idx === activities.length - 1}>
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="w-7 h-7"
                          onClick={() => toggleActivity(idx)}
                          aria-label={a.active === false ? 'wieder aktivieren' : 'deaktivieren'}
                          title={a.active === false ? 'wieder aktivieren' : (a.id ? 'deaktivieren (Buchungen behalten die Tätigkeit)' : 'entfernen')}
                        >
                          {a.active === false ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Button type="button" variant="outline" size="sm" onClick={addActivity}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            Tätigkeit hinzufügen
          </Button>
          <p className="text-xs text-muted-foreground">
            Datum = Buchungsdatum hinter der Tätigkeit, Notiz = Notiz der Buchung. Tätigkeiten werden nicht gelöscht, nur deaktiviert.
          </p>
        </div>
      )}
    </div>
  );
}
